import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { editorOptions } from '../../../market-fan-exploration/models/editor-config/editor-config';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

declare const monaco: any;

@Component({
  selector: 'app-monaco-editor',
  templateUrl: './monaco-editor.component.html',
  styleUrl: './monaco-editor.component.scss',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MonacoEditorComponent), multi: true }],
})
export class MonacoEditorComponent implements AfterViewInit, OnDestroy {
  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef;
  @Input() set readOnly(value: boolean) {
    this._readOnly = value;
    this.editorInstance?.updateOptions({ readOnly: value });
  }
  @Input() expanded = false;
  @Input() set value(value: string | null | undefined) {
    this._value = value || '';
    this.updateEditorValue();
  }
  isLoading = true;
  private editorInstance: any;
  private resizeObserver?: ResizeObserver;
  private _value = '';
  private _readOnly = false;

  // Required for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (_: any) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};
  decorationsId: string[] = [];

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.editorInstance?.layout());
      this.resizeObserver.observe(this.editorContainer.nativeElement);
    }
    this.loadMonaco();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.editorInstance) {
      this.editorInstance.dispose();
    }
  }

  private loadMonaco() {
    if (!(window as any).require) {
      console.error('RequireJS not loaded. loader.js may be missing!');
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      return;
    }

    (window as any).require.config({ paths: { vs: 'assets/monaco/vs' } });

    (window as any).require(
      ['vs/editor/editor.main'],
      () => {
        console.log('Monaco loaded successfully!');
        this.editorInstance = monaco.editor.create(this.editorContainer.nativeElement, {
          value: this._value || '',
          readOnly: this._readOnly,
          ...editorOptions,
        });
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
        requestAnimationFrame(() => this.editorInstance?.layout());

        const model = this.editorInstance.getModel();
        if (model && this._value !== model.getValue()) {
          model.setValue(this._value);
        }

        // ✨ Emit changes to form control
        this.editorInstance.onDidChangeModelContent(() => {
          const currentValue = this.editorInstance.getValue();
          if (currentValue !== this._value) {
            this._value = currentValue;
            this.onChange(currentValue);
          }
        });

        // ✨ Mark as touched on blur
        this.editorInstance.onDidBlurEditorWidget(() => {
          this.onTouched();
        });
      },
      (error: unknown) => {
        console.error('Unable to load Monaco editor.', error);
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      },
    );
  }

  // ControlValueAccessor Methods
  writeValue(value: string): void {
    this._value = value || '';
    this.updateEditorValue();
  }

  private updateEditorValue(): void {
    const model = this.editorInstance?.getModel();
    if (model && this._value !== model.getValue()) {
      model.setValue(this._value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.editorInstance) {
      this.editorInstance.updateOptions({ readOnly: isDisabled });
    }
  }

  navigateToError(line: number, column = 1): void {
    if (!this.editorInstance) return;

    this.editorInstance.revealPositionInCenter({ lineNumber: line, column });
    this.editorInstance.setPosition({ lineNumber: line, column });
    this.editorInstance.focus();
  }

  highlightErrorLine(line: number): void {
    if (!this.editorInstance) return;
    this.decorationsId = this.editorInstance.deltaDecorations(this.decorationsId || [], [
      {
        range: new monaco.Range(line, 1, line, 1),
        options: {
          isWholeLine: true,
        },
      },
    ]);
  }

  getQueryStartLine(): number {
    if (!this.editorInstance) return 1;
    const model = this.editorInstance.getModel();
    if (!model) return 1;
    const lines = model.getLinesContent();
    let inBlock = false;
    for (let i = 0; i < lines.length; i++) {
      const trimmed = (lines[i] ?? '').trim();
      if (inBlock) {
        if (trimmed.includes('*/')) inBlock = false;
        continue;
      }
      if (!trimmed) continue;
      if (trimmed.startsWith('--')) continue;
      if (trimmed.startsWith('/*')) {
        if (!trimmed.includes('*/')) inBlock = true;
        continue;
      }
      return i + 1;
    }
    return 1;
  }

  translateAthenaLine(athenaLine: number): number {
    const start = this.getQueryStartLine();
    return start + athenaLine - 1;
  }
}
